/**
 * useTicketsGridState — URL-backed grid state for the tickets list (US4/US5/US6, Feature 039).
 * URL is the single source of truth for sortBy, sortDir, page, and filter[col] params.
 */

import { useSearchParams } from 'react-router-dom';
import { useCallback } from 'react';

const ALLOWED_SORT_COLS = new Set([
  'createdAt', 'updatedAt', 'status', 'companyName', 'subject', 'owner', 'priority', 'customerResponded',
]);

const ALLOWED_FILTER_COLS = new Set([
  'status', 'priority', 'category', 'owner', 'companyName', 'subject', 'customerResponded',
]);

export interface TicketsGridState {
  sortBy: string | null;
  sortDir: 'asc' | 'desc' | null;
  page: number;
  filters: Record<string, string>;
  setSort: (col: string, dir: 'asc' | 'desc' | null) => void;
  setPage: (page: number) => void;
  setFilter: (col: string, values: string[] | null) => void;
  clearFilter: (col: string) => void;
  clearAllFilters: () => void;
}

export function useTicketsGridState(): TicketsGridState {
  const [searchParams, setSearchParams] = useSearchParams();

  const rawSortBy = searchParams.get('sortBy');
  const rawSortDir = searchParams.get('sortDir');
  const rawPage = searchParams.get('page');

  // Validate and sanitise — unknown sortBy → null (default applied by endpoint)
  const sortBy = rawSortBy && ALLOWED_SORT_COLS.has(rawSortBy) ? rawSortBy : null;
  const sortDir: 'asc' | 'desc' | null =
    rawSortDir === 'asc' || rawSortDir === 'desc' ? rawSortDir : null;
  const page = rawPage ? Math.max(1, parseInt(rawPage, 10)) : 1;

  // Read filter[col]=val URL params, stripping unknown keys
  const filters: Record<string, string> = {};
  searchParams.forEach((value, key) => {
    const match = key.match(/^filter\[(.+)\]$/);
    if (match) {
      const col = match[1];
      if (ALLOWED_FILTER_COLS.has(col)) {
        filters[col] = value;
      }
    }
  });

  const setSort = useCallback(
    (col: string, dir: 'asc' | 'desc' | null) => {
      setSearchParams(prev => {
        const next = new URLSearchParams(prev);
        if (dir === null) {
          next.delete('sortBy');
          next.delete('sortDir');
        } else {
          next.set('sortBy', col);
          next.set('sortDir', dir);
        }
        next.set('page', '1'); // reset to first page on sort change
        return next;
      });
    },
    [setSearchParams],
  );

  const setPage = useCallback(
    (p: number) => {
      setSearchParams(prev => {
        const next = new URLSearchParams(prev);
        next.set('page', String(p));
        return next;
      });
    },
    [setSearchParams],
  );

  const setFilter = useCallback(
    (col: string, values: string[] | null) => {
      setSearchParams(prev => {
        const next = new URLSearchParams(prev);
        if (!values || values.length === 0) {
          next.delete(`filter[${col}]`);
        } else {
          next.set(`filter[${col}]`, values.join(','));
        }
        next.set('page', '1'); // reset page on filter change
        return next;
      });
    },
    [setSearchParams],
  );

  const clearFilter = useCallback(
    (col: string) => {
      setSearchParams(prev => {
        const next = new URLSearchParams(prev);
        next.delete(`filter[${col}]`);
        next.set('page', '1');
        return next;
      });
    },
    [setSearchParams],
  );

  const clearAllFilters = useCallback(() => {
    setSearchParams(prev => {
      const next = new URLSearchParams(prev);
      Array.from(next.keys())
        .filter(k => k.startsWith('filter['))
        .forEach(k => next.delete(k));
      next.set('page', '1');
      return next;
    });
  }, [setSearchParams]);

  return { sortBy, sortDir, page, filters, setSort, setPage, setFilter, clearFilter, clearAllFilters };
}
